/**
 * SA 顧客保有期間 (customer retention) のロジック (純関数)。
 * functions / admin-web の双方で同一ルールを使うため shared に集約。
 *
 * 仕様 (2026-07-28):
 *  - SA が獲得した顧客は、獲得時点から N ヶ月だけその SA の顧客とみなす。
 *  - その SA の商談 (Deal.salesAgentUid === 顧客の ownerSaUid) で入金があると、
 *    入金時点を起算に N ヶ月へリセットされる。
 *  - SA が紐づかない商談の入金ではリセットしない。
 *  - 期限を過ぎた顧客は ownerSaUid が外れ、弊社 (社内) の顧客になる。
 *  - N = 0 は「無期限」(この機能を実質 OFF にする) を意味する。
 *
 * 期限は「JST のその日の終わり」に丸める。時刻単位の端数で境界が揺れるのを避けるため。
 */

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

/** 顧客保有期間の既定値 (ヶ月)。 */
export const SA_RETENTION_DEFAULT_MONTHS = 12;
/** 期限切れ間近リマインダーの既定値 (日前)。 */
export const SA_RETENTION_DEFAULT_REMINDER_DAYS = 30;

/** JST の暦日 (年/月/日) に分解する。 */
function toJstParts(d: Date): { y: number; m: number; day: number } {
  const jst = new Date(d.getTime() + JST_OFFSET_MS);
  return {
    y: jst.getUTCFullYear(),
    m: jst.getUTCMonth() + 1,
    day: jst.getUTCDate(),
  };
}

/** その年月の日数 (1-12 月指定)。 */
function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** JST の暦日 (y-m-day) の「終わり」= 翌日 00:00 JST の 1ms 前を UTC Date で返す。 */
function endOfJstDay(y: number, m: number, day: number): Date {
  return new Date(Date.UTC(y, m - 1, day + 1) - JST_OFFSET_MS - 1);
}

/**
 * 起算日時から N ヶ月後の「JST 日付」の終わりを返す。
 * 月末は繰り上がらずクランプする (1/31 + 1ヶ月 = 2/28、2/29 は閏年のみ)。
 * months <= 0 は無期限とみなし null を返す。
 */
export function computeRetentionUntil(
  from: Date,
  months: number,
): Date | null {
  if (!Number.isFinite(months) || months <= 0) return null;
  const span = Math.floor(months);
  const { y, m, day } = toJstParts(from);

  // 月を進めてから、進めた先の月に存在しない日はその月の末日へクランプ。
  const totalMonths = (y * 12 + (m - 1)) + span;
  const ty = Math.floor(totalMonths / 12);
  const tm = (totalMonths % 12) + 1;
  const tday = Math.min(day, daysInMonth(ty, tm));

  return endOfJstDay(ty, tm, tday);
}

/**
 * 期限までの残り日数 (JST の暦日基準)。当日 = 0、過ぎていれば負。
 * `until` は endOfJstDay で作られている前提だが、任意の時刻でも暦日で比較する。
 */
export function daysUntilRetentionEnd(until: Date, now: Date): number {
  const u = toJstParts(until);
  const n = toJstParts(now);
  const uMs = Date.UTC(u.y, u.m - 1, u.day);
  const nMs = Date.UTC(n.y, n.m - 1, n.day);
  return Math.round((uMs - nMs) / DAY_MS);
}

export type SaRetentionState =
  /** 担当SA なし、または無期限 (期限管理の対象外) */
  | 'none'
  /** 期限内 */
  | 'active'
  /** 期限が近い (リマインダー日数以内) */
  | 'expiring'
  /** 期限切れ (スイープ待ち or 解除済み) */
  | 'expired';

/**
 * 顧客の保有状態を判定する。UI のラベル表示と sweep の双方で使う。
 */
export function resolveRetentionState(args: {
  /** 担当SA が居るか */
  hasOwnerSa: boolean;
  /** 保有期限。null/undefined = 無期限 */
  until: Date | null | undefined;
  now: Date;
  /** 期限切れ間近とみなす日数。既定 30。 */
  reminderDays?: number;
}): { state: SaRetentionState; daysLeft: number | null } {
  const { hasOwnerSa, until, now } = args;
  if (!hasOwnerSa || !until) return { state: 'none', daysLeft: null };
  const daysLeft = daysUntilRetentionEnd(until, now);
  if (until.getTime() <= now.getTime()) return { state: 'expired', daysLeft };
  const reminderDays = args.reminderDays ?? SA_RETENTION_DEFAULT_REMINDER_DAYS;
  if (daysLeft <= reminderDays) return { state: 'expiring', daysLeft };
  return { state: 'active', daysLeft };
}

/** 保有期限をリセット/設定した理由。 */
export type SaRetentionReason =
  /** SA に紐付いた時点 (フォーム経由 or SA 手動追加) */
  | 'assigned'
  /** その SA の商談で入金があった */
  | 'payment'
  /** 管理者が手動で延長した */
  | 'manual_extend';

export const SA_RETENTION_REASON_LABEL: Record<SaRetentionReason, string> = {
  assigned: 'SA が獲得',
  payment: 'SA商談の入金',
  manual_extend: '手動延長',
};
